import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, MessageCircle } from "lucide-react";
import PageHero from "../components/sections/PageHero";

const WHATSAPP_BUILD =
  "https://wa.me/918122431120?text=Hi%20Deployedd%20Labs%20%E2%80%94%20I%27d%20like%20to%20build%20with%20you.";
const WHATSAPP_COLLAB =
  "https://wa.me/918122431120?text=Hi%20Deployedd%20Labs%20%E2%80%94%20I%27d%20like%20to%20explore%20a%20collaboration.";

const domains = [
  {
    title: "HealthTech & MedTech",
    desc: "Exploring future human experiences through wellness, comfort, and intelligent interactions.",
  },
  {
    title: "Sustainability & Circular Systems",
    desc: "Creating systems that enable smarter, more responsible futures.",
  },
  {
    title: "Intelligent Experiences",
    desc: "Reimagining how technology integrates with everyday life.",
  },
];

const initiatives = [
  {
    name: "ORTIQ™",
    domain: "HealthTech",
    theme: "Intelligent Human Experiences",
    desc: "Exploring new possibilities at the intersection of people, technology, and future experiences.",
    status: "Lab Initiative",
  },
  {
    name: "VOXCURE™",
    domain: "MedTech",
    theme: "Human Expression · Intelligent Experiences",
    desc: "Investigating future-forward experiences designed around human interaction and wellbeing.",
    status: "Exploration Program",
  },
  {
    name: "GLUMORA™",
    domain: "MedTech",
    theme: "Visual Experiences · Human Comfort · Intelligent Wearables",
    desc: "Exploring future experiences that connect comfort, experience, and intelligent interaction.",
    status: "Internal Development",
  },
  {
    name: "AGRINOVA™",
    domain: "Sustainability",
    theme: "Circular Systems · Resource Transformation · Future Ecosystems",
    desc: "Exploring intelligent approaches to enabling sustainable ecosystems and future value creation.",
    status: "Concept Validation",
  },
];

const stages = ["Observe", "Explore", "Design", "Build", "Validate", "DEPLOY", "Scale"];

const collaborators = [
  "Universities",
  "Manufacturing Ecosystems",
  "Corporates",
  "Researchers",
  "Product Development Partners",
];

const BuiltAtLabs = () => (
  <div data-testid="built-at-labs-page">
    <PageHero
      kicker="/ Built at Labs"
      title="Built at"
      accent="Deployedd Labs."
      breadcrumb={["Built at Labs"]}
      lead="From exploration to real-world outcomes. Deployedd Labs is a venture lab that builds products through innovation and scales impact through capability building. We explore emerging possibilities across human experiences, sustainability and intelligent systems — transforming ideas into future-facing ventures through research, experimentation and thoughtful design."
    />

    {/* Hero CTAs */}
    <section className="container-wide pb-24 md:pb-32 border-b border-ink">
      <div className="flex flex-wrap gap-3">
        <a
          href={WHATSAPP_BUILD}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="builtatlabs-cta-build"
          className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9F381A] transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> Build with us <ArrowUpRight className="w-4 h-4" />
        </a>
        <a
          href="#initiatives"
          data-testid="builtatlabs-cta-explore"
          className="inline-flex items-center gap-2 border border-ink px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-ink hover:text-white transition-colors"
        >
          Explore our initiatives <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </section>

    {/* Domains We Explore */}
    <section data-testid="domains-section" className="container-wide py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-5">
          <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ 01 — Where we look</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Domains <em className="font-editorial italic text-[#9F381A]">we explore.</em>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <p className="text-lg text-ink-muted leading-relaxed">
            Three intersecting fields where we read the signal early, test thoughtful directions, and move ideas towards real-world outcomes.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-ink">
        {domains.map((d, i) => (
          <div
            key={d.title}
            data-testid={`domain-${i}`}
            className="border-r border-b border-ink p-10 md:p-14 group hover:bg-[#F3F3F1] transition-colors"
          >
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#9F381A] mb-10">/ 0{i + 1}</p>
            <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-4 group-hover:translate-x-1 transition-transform">
              {d.title}
            </h3>
            <p className="text-base text-ink-muted leading-relaxed">{d.desc}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Current Lab Initiatives */}
    <section id="initiatives" data-testid="initiatives-section" className="bg-ink text-white py-24 md:py-32">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12 items-start">
          <div className="lg:col-span-6">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E] mb-6">/ 02 — Current initiatives</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Currently in <em className="font-editorial italic text-[#E68A6E]">the lab.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-white/40 mb-4">/ Disclosure</p>
            <p className="text-sm text-white/65 leading-relaxed">
              Presented intentionally at a directional level. Detailed implementation and technical information are intentionally not publicly disclosed.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/15">
          {initiatives.map((it, i) => (
            <div
              key={it.name}
              data-testid={`initiative-${i}`}
              className="border-r border-b border-white/15 p-8 md:p-10 group hover:bg-white/5 transition-colors flex flex-col"
            >
              <div className="flex items-center justify-between mb-10">
                <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E]">/ 0{i + 1}</p>
                <span className="inline-flex items-center gap-2 px-2.5 py-1 border border-white/30 text-[10px] uppercase tracking-[0.2em] text-white/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E68A6E]" /> {it.status}
                </span>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl tracking-tight leading-none mb-6">{it.name}</h3>
              <div className="space-y-1 mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">Domain · {it.domain}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-white/40">{it.theme}</p>
              </div>
              <p className="text-sm text-white/70 leading-relaxed flex-1">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Our Venture Journey */}
    <section data-testid="journey-section" className="container-wide py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-6">
          <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ 03 — How we move</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Our <em className="font-editorial italic text-[#9F381A]">venture journey.</em>
          </h2>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <p className="text-lg text-ink-muted leading-relaxed">
            A linear arc from quiet observation to compounding scale — with DEPLOY as the moment a lab idea becomes a real-world outcome.
          </p>
        </div>
      </div>

      <div data-testid="journey-flow" className="border-t border-ink pt-12">
        <ol className="flex flex-wrap items-stretch gap-y-6">
          {stages.map((s, i) => {
            const isDeploy = s === "DEPLOY";
            return (
              <li
                key={s}
                data-testid={`stage-${s.toLowerCase()}`}
                className="flex items-center"
              >
                <div
                  className={`px-5 md:px-6 py-3.5 text-[11px] md:text-xs uppercase tracking-[0.25em] border ${
                    isDeploy
                      ? "bg-[#9F381A] text-white border-[#9F381A] font-medium"
                      : "border-ink text-ink"
                  }`}
                >
                  {s}
                </div>
                {i < stages.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="mx-3 md:mx-4 font-mono-cust text-sm text-gray-400"
                  >
                    →
                  </span>
                )}
              </li>
            );
          })}
        </ol>
        <p className="mt-10 font-mono-cust text-[11px] uppercase tracking-[0.25em] text-gray-500 max-w-xl">
          / DEPLOY — the transition from lab to impact.
        </p>
      </div>
    </section>

    {/* Why We Build */}
    <section data-testid="why-section" className="container-wide pb-24 md:pb-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 border-t border-ink pt-16">
        <div className="lg:col-span-4">
          <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ 04 — Why we build</p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tighter leading-[0.95]">
            Innovation,<br />
            <em className="font-editorial italic text-[#9F381A]">made meaningful.</em>
          </h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg md:text-xl text-ink-muted leading-relaxed">
          <p>
            We believe innovation becomes meaningful when ideas evolve into real-world outcomes.
          </p>
          <p>
            Capability building exists to accelerate adoption, partnerships, and future ecosystems around innovation.
          </p>
          <p className="font-editorial italic text-2xl md:text-3xl text-ink leading-snug border-l-2 border-[#9F381A] pl-6">
            Learning is not the destination — it is the catalyst.
          </p>
        </div>
      </div>
    </section>

    {/* Collaborate */}
    <section data-testid="collaborate-section" className="bg-ink text-white py-24 md:py-32">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12 items-end">
          <div className="lg:col-span-7">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E] mb-6">/ 05 — Collaborate</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
              Collaborate with <em className="font-editorial italic text-[#E68A6E]">Deployedd Labs.</em>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed max-w-xl">
              Building meaningful outcomes through ecosystems and partnerships.
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <a
              href={WHATSAPP_COLLAB}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="collaborate-cta"
              className="inline-flex items-center justify-between w-full md:w-auto gap-3 bg-white text-ink px-6 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#E68A6E] hover:text-white transition-colors"
            >
              Build with us <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 border-t border-l border-white/15">
          {collaborators.map((c, i) => (
            <div
              key={c}
              data-testid={`collaborator-${i}`}
              className="border-r border-b border-white/15 p-8 md:p-10 hover:bg-white/5 transition-colors"
            >
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E] mb-6">/ 0{i + 1}</p>
              <p className="font-heading text-xl md:text-2xl tracking-tight leading-tight">{c}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Footer note */}
    <section data-testid="footer-note" className="container-wide py-16 md:py-20 border-t border-ink">
      <p className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-4">/ Note</p>
      <p className="text-sm md:text-base text-ink-muted leading-relaxed max-w-3xl">
        Select initiatives represented on this page communicate exploratory directions and venture themes developed at Deployedd Labs. Technical, operational and implementation details are intentionally not publicly disclosed.
      </p>
    </section>

    <div className="hidden">
      <Link to="/contact">contact</Link>
    </div>
  </div>
);

export default BuiltAtLabs;
